import type { UseQueryOptions } from '@tanstack/react-query'

/**
 * Shared lifecycle policy for a Shopify resource query.
 *
 * These conservative baseline values preserve the query inventory. Stable
 * reference data may be reused briefly, while operational resources remain
 * stale by default so explicit polling and invalidation stay in charge.
 * Product changes should update measured resource needs rather than extending
 * every lifetime to hide requests.
 */
export type ShopifyQueryPolicy = Pick<
  UseQueryOptions<unknown, Error, unknown, readonly unknown[]>,
  'staleTime' | 'gcTime' | 'refetchOnMount' | 'refetchOnWindowFocus' | 'refetchOnReconnect'
>

const staleOnlyLifecycle = {
  gcTime: 5 * 60 * 1000,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const

export const shopifyQueryPolicies = {
  audience: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  connection: { staleTime: 5 * 60 * 1000, ...staleOnlyLifecycle },
  entitlements: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  readiness: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  usage: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  usageActivity: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  productGrant: { staleTime: 30 * 1000, ...staleOnlyLifecycle },
  productChanges: { staleTime: 0, ...staleOnlyLifecycle },
  integrations: { staleTime: 0, ...staleOnlyLifecycle },
  microSurveys: { staleTime: 0, ...staleOnlyLifecycle },
  pixelActivity: { staleTime: 30 * 1000, ...staleOnlyLifecycle },
  builderBootstrap: { staleTime: 5 * 60 * 1000, ...staleOnlyLifecycle },
  studyHistory: { staleTime: 60 * 1000, ...staleOnlyLifecycle },
  studyResults: { staleTime: 5 * 60 * 1000, ...staleOnlyLifecycle },
  studyActivity: { staleTime: 0, ...staleOnlyLifecycle },
  deletionStatus: { staleTime: 0, ...staleOnlyLifecycle },
  deletionFeedback: { staleTime: 0, ...staleOnlyLifecycle },
  runPreflight: {
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  intelligence: { staleTime: 5 * 60 * 1000, ...staleOnlyLifecycle },
  syncStatus: { staleTime: 5 * 60 * 1000, ...staleOnlyLifecycle },
  /**
   * Authoring surfaces (list, run source, results) whose freshness is owned by polling or
   * explicit invalidation after a mutation, not by a lifetime. Declaring this keeps the
   * intent visible instead of inheriting a zero from the client.
   */
  studyAuthoring: { staleTime: 0, ...staleOnlyLifecycle },
  /** Store-knowledge reads vary by input and are invalidated when the merchant edits them. */
  storeKnowledge: { staleTime: 0, ...staleOnlyLifecycle },
  /** Opportunity setups are re-derived server-side as a study is authored. */
  insightSetups: { staleTime: 0, ...staleOnlyLifecycle },
} satisfies Record<string, ShopifyQueryPolicy>

/** Compatibility alias for callers that still import the audience duration. */
export const SHOPIFY_AUDIENCE_QUERY_STALE_TIME_MS = shopifyQueryPolicies.audience.staleTime
